/**
 * NavigatorX Referral Tree Viewer
 *
 * Interactive referral chain visualization:
 * - Multi-level referral tree display
 * - Per-node status tracking (invited, signed up, subscribed, churned)
 * - Credit flow visualization
 * - Search for specific referrers
 * - Tree depth and branching metrics
 * - Expandable/collapsible tree nodes
 *
 * @author Claude AI for TGE.LA
 * @version 1.0.0
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import {
  GitBranch,
  Users,
  UserPlus,
  ChevronRight,
  ChevronDown,
  RefreshCw,
  Loader2,
  Search,
  Gift,
  CheckCircle,
  XCircle,
  Clock,
  Crown,
  TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit
} from 'firebase/firestore';
import { navDb } from '../../../utils/firebase/client';
import {
  NAV_USERS_COLLECTION,
  timestampToDate,
  getUserReferralStats,
  type ReferralStats
} from '../../../utils/services/navigatorXService';
import type { NavUser } from '../../../types/navigatorx';

interface ReferralTreeViewerProps {
  embedded?: boolean;
}

interface TreeNode {
  user: NavUser;
  children: TreeNode[];
  depth: number;
  referralStats?: ReferralStats;
}

export default function ReferralTreeViewer({ embedded = false }: ReferralTreeViewerProps) {
  const [loading, setLoading] = useState(true);
  const [allUsers, setAllUsers] = useState<NavUser[]>([]);
  const [rootNodes, setRootNodes] = useState<TreeNode[]>([]);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [treeStats, setTreeStats] = useState({
    totalReferrers: 0,
    totalReferred: 0,
    maxDepth: 0,
    avgBranchingFactor: 0,
    totalCreditsEarned: 0
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const usersSnapshot = await getDocs(collection(navDb, NAV_USERS_COLLECTION));
      const users: NavUser[] = [];

      usersSnapshot.docs.forEach(doc => {
        if (['blocked', 'invites', 'admin_logs'].includes(doc.id)) return;
        users.push({ id: doc.id, ...doc.data() } as NavUser);
      });

      setAllUsers(users);

      // Build referral tree
      const userMap = new Map<string, NavUser>();
      users.forEach(u => userMap.set(u.id, u));

      // Find root referrers (users who have referrals but were not referred themselves)
      const referredUserIds = new Set(
        users.filter(u => u.referredBy).map(u => u.referredBy!)
      );
      const rootReferrers = users.filter(u =>
        (u.referralCode || (u.referralCount && u.referralCount > 0)) &&
        !u.referredBy
      );

      // Build tree recursively
      const buildTree = (userId: string, depth: number): TreeNode => {
        const user = userMap.get(userId)!;
        const children = users
          .filter(u => u.referredBy === userId)
          .map(child => buildTree(child.id, depth + 1));
        return { user, children, depth };
      };

      const trees = rootReferrers
        .map(u => buildTree(u.id, 0))
        .filter(tree => tree.children.length > 0 || (tree.user.referralCount && tree.user.referralCount > 0))
        .sort((a, b) => b.children.length - a.children.length);

      setRootNodes(trees);

      // Expand top referrers by default
      const defaultExpanded = new Set<string>();
      trees.slice(0, 5).forEach(tree => defaultExpanded.add(tree.user.id));
      setExpandedNodes(defaultExpanded);

      // Calculate tree stats
      const totalReferred = users.filter(u => u.referredBy).length;
      const referrersWithChildren = trees.filter(t => t.children.length > 0);
      const allBranching = referrersWithChildren.map(t => t.children.length);
      const avgBranching = allBranching.length > 0
        ? allBranching.reduce((s, v) => s + v, 0) / allBranching.length
        : 0;

      const getMaxDepth = (node: TreeNode): number => {
        if (node.children.length === 0) return node.depth;
        return Math.max(...node.children.map(getMaxDepth));
      };
      const maxDepth = trees.length > 0 ? Math.max(...trees.map(getMaxDepth)) : 0;

      const totalCredits = users.reduce((sum, u) => sum + (u.totalCreditsEarned || 0), 0);

      setTreeStats({
        totalReferrers: referredUserIds.size,
        totalReferred,
        maxDepth,
        avgBranchingFactor: avgBranching,
        totalCreditsEarned: totalCredits
      });
    } catch (error) {
      console.error('Error loading referral tree:', error);
      toast.error('Failed to load referral tree');
    } finally {
      setLoading(false);
    }
  };

  const toggleNode = (userId: string) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const expandAll = () => {
    const all = new Set<string>();
    const addAll = (nodes: TreeNode[]) => {
      nodes.forEach(n => {
        all.add(n.user.id);
        addAll(n.children);
      });
    };
    addAll(rootNodes);
    setExpandedNodes(all);
  };

  const collapseAll = () => {
    setExpandedNodes(new Set());
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'SUBSCRIBED':
      case 'active':
        return 'bg-green-100 text-green-700';
      case 'TRIAL_ACTIVE':
      case 'trial':
        return 'bg-blue-100 text-blue-700';
      case 'TRIAL_EXPIRED':
      case 'SUBSCRIPTION_EXPIRED':
      case 'expired':
        return 'bg-red-100 text-red-700';
      case 'REFERRAL_FREE_MONTH':
        return 'bg-pink-100 text-pink-700';
      case 'BLOCKED':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getCountDescendants = (node: TreeNode): number => {
    return node.children.length + node.children.reduce((sum, c) => sum + getCountDescendants(c), 0);
  };

  const filterTree = (nodes: TreeNode[], term: string): TreeNode[] => {
    if (!term) return nodes;
    const lower = term.toLowerCase();
    const matches = (node: TreeNode): boolean => {
      const nameMatch = node.user.displayName?.toLowerCase().includes(lower);
      const emailMatch = node.user.email?.toLowerCase().includes(lower);
      const codeMatch = node.user.referralCode?.toLowerCase().includes(lower);
      const childMatch = node.children.some(matches);
      return nameMatch || emailMatch || codeMatch || childMatch || false;
    };
    return nodes.filter(matches);
  };

  const renderTreeNode = (node: TreeNode) => {
    const isExpanded = expandedNodes.has(node.user.id);
    const hasChildren = node.children.length > 0;
    const totalDescendants = getCountDescendants(node);

    return (
      <div key={node.user.id} className="relative">
        {/* Connection line */}
        {node.depth > 0 && (
          <div
            className="absolute left-0 top-0 w-6 border-l-2 border-b-2 border-gray-200 rounded-bl"
            style={{ height: '24px', marginLeft: `${(node.depth - 1) * 28 + 12}px` }}
          />
        )}

        <div
          className={`flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors ${
            hasChildren ? '' : 'ml-5'
          }`}
          style={{ paddingLeft: `${node.depth * 28 + 4}px` }}
          onClick={() => hasChildren && toggleNode(node.user.id)}
        >
          {/* Expand/collapse */}
          {hasChildren ? (
            isExpanded ? (
              <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
            ) : (
              <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
            )
          ) : (
            <div className="w-4" />
          )}

          {/* User avatar */}
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
            node.depth === 0 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'
          }`}>
            {node.user.displayName?.charAt(0) || node.user.email?.charAt(0) || '?'}
          </div>

          {/* User info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium truncate">
                {node.user.displayName || node.user.email || 'Unknown'}
              </span>
              <Badge className={`text-xs ${getStatusColor(node.user.subscriptionStatus)}`}>
                {node.user.subscriptionStatus}
              </Badge>
              {node.user.referralCode && (
                <code className="text-xs bg-gray-100 px-1 rounded">{node.user.referralCode}</code>
              )}
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              {node.user.referredByCode && (
                <span>via <code className="bg-gray-50 px-1 rounded">{node.user.referredByCode}</code></span>
              )}
              {(node.user.totalCreditsEarned || 0) > 0 && (
                <span className="text-green-600">
                  <Gift className="h-3 w-3 inline mr-0.5" />
                  {node.user.totalCreditsEarned} credits
                </span>
              )}
            </div>
          </div>

          {/* Children count */}
          {hasChildren && (
            <Badge variant="outline" className="text-xs flex-shrink-0">
              {node.children.length} direct
              {totalDescendants > node.children.length && ` / ${totalDescendants} total`}
            </Badge>
          )}
        </div>

        {/* Children */}
        {isExpanded && hasChildren && (
          <div>
            {node.children.map(child => renderTreeNode(child))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center ${embedded ? 'py-12' : 'min-h-[400px]'}`}>
        <Loader2 className="h-8 w-8 animate-spin text-green-500" />
      </div>
    );
  }

  const filteredTrees = filterTree(rootNodes, searchTerm);

  return (
    <div className={embedded ? '' : 'p-6'}>
      {/* Header */}
      {!embedded && (
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Referral Tree</h2>
            <p className="text-gray-500">Visualize referral chains and credit flow</p>
          </div>
          <Button variant="outline" onClick={loadData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Card>
          <CardContent className="p-4 text-center">
            <Users className="h-5 w-5 text-green-500 mx-auto mb-1" />
            <p className="text-xl font-bold text-gray-700">{treeStats.totalReferrers}</p>
            <p className="text-xs text-gray-500">Active Referrers</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <UserPlus className="h-5 w-5 text-blue-500 mx-auto mb-1" />
            <p className="text-xl font-bold text-gray-700">{treeStats.totalReferred}</p>
            <p className="text-xs text-gray-500">Referred Users</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <GitBranch className="h-5 w-5 text-purple-500 mx-auto mb-1" />
            <p className="text-xl font-bold text-gray-700">{treeStats.maxDepth}</p>
            <p className="text-xs text-gray-500">Max Tree Depth</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <TrendingUp className="h-5 w-5 text-orange-500 mx-auto mb-1" />
            <p className="text-xl font-bold text-gray-700">{treeStats.avgBranchingFactor.toFixed(1)}</p>
            <p className="text-xs text-gray-500">Avg Referrals/User</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Gift className="h-5 w-5 text-pink-500 mx-auto mb-1" />
            <p className="text-xl font-bold text-gray-700">{treeStats.totalCreditsEarned}</p>
            <p className="text-xs text-gray-500">Total Credits</p>
          </CardContent>
        </Card>
      </div>

      {/* Search + Controls */}
      <div className="flex gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search by name, email, or referral code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button variant="outline" size="sm" onClick={expandAll}>
          Expand All
        </Button>
        <Button variant="outline" size="sm" onClick={collapseAll}>
          Collapse All
        </Button>
      </div>

      {/* Tree */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2">
            <GitBranch className="h-5 w-5 text-green-500" />
            Referral Chains ({filteredTrees.length} root referrers)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredTrees.length === 0 ? (
            <p className="text-center text-gray-500 py-8">
              {searchTerm ? 'No matching referral chains found' : 'No referral chains yet'}
            </p>
          ) : (
            <div className="space-y-1 max-h-[600px] overflow-y-auto">
              {filteredTrees.map(tree => renderTreeNode(tree))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
